import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js'

import { Node, getRef } from './Node'
import * as THREE from '../extras/three'
import { isBoolean } from 'lodash-es'

const defaults = {
  object3d: null,
  animations: [],
  url: null,
  castShadow: true,
  receiveShadow: true,
}

const m1 = new THREE.Matrix4()

const defaultStopOpts = { fade: 0.15 }

export class SkinnedMesh extends Node {
  constructor(data = {}) {
    super(data)
    this.name = 'skinnedmesh'

    this._object3d = data.object3d
    this._animations = data.animations
    this._url = data.url

    this.castShadow = data.castShadow
    this.receiveShadow = data.receiveShadow

    this.clips = {}
    this.actions = {}
    this.bones = null
    this.animNames = []
    this.boneHandles = {}
    this._audioReactiveMeshes = null
  }

  mount() {
    this.clips = {}
    this.actions = {}
    this.bones = null
    this.animNames = []
    this.boneHandles = {}
    this.obj = SkeletonUtils.clone(this._object3d)
    this.obj.matrixWorld.copy(this.matrixWorld)
    this.obj.matrixAutoUpdate = false
    this.obj.matrixWorldAutoUpdate = false
    this.obj.traverse(n => {
      if (n.isMesh) {
        n.castShadow = this._castShadow
        n.receiveShadow = this._receiveShadow
        // bind-pose bounds don't cover posed bones — lights/beams vanish at some angles
        n.frustumCulled = false
      }
    })
    this.ctx.world.stage.scene.add(this.obj)
    for (const clip of this._animations) {
      this.clips[clip.name] = clip
      this.animNames.push(clip.name)
    }
    this.needsRebuild = false
  }

  commit(didMove) {
    if (this.needsRebuild) {
      this.unmount()
      this.mount()
    }
    if (didMove) {
      if (this.obj) {
        this.obj.matrixWorld.copy(this.matrixWorld)
      }
    }
  }

  unmount() {
    if (this.obj) {
      if (this.mixer) {
        this.mixer.stopAllAction()
        this.mixer.uncacheRoot(this.obj)
        this.mixer = null
        this.ctx.world.setHot(this, false)
        this.clips = {}
        this.actions = {}
      }
      this.ctx.world.stage.scene.remove(this.obj)
      this.obj = null
      this.bones = null
      this.animNames = []
      this.boneHandles = {}
    }
  }

  update(delta) {
    this.mixer?.update(delta)
  }

  copy(source, recursive) {
    super.copy(source, recursive)
    this._object3d = source._object3d
    this._animations = source._animations
    this._url = source._url
    this._castShadow = source._castShadow
    this._receiveShadow = source._receiveShadow
    return this
  }

  get anims() {
    return this.animNames.slice()
  }

  get url() {
    return this._url
  }

  get castShadow() {
    return this._castShadow
  }

  set castShadow(value = defaults.castShadow) {
    if (!isBoolean(value)) {
      throw new Error('[skinnedmesh] castShadow not a boolean')
    }
    if (this._castShadow === value) return
    this._castShadow = value
    if (this.handle) {
      this.needsRebuild = true
      this.setDirty()
    }
  }

  get receiveShadow() {
    return this._receiveShadow
  }

  set receiveShadow(value = defaults.receiveShadow) {
    if (!isBoolean(value)) {
      throw new Error('[skinnedmesh] receiveShadow not a boolean')
    }
    if (this._receiveShadow === value) return
    this._receiveShadow = value
    if (this.handle) {
      this.needsRebuild = true
      this.setDirty()
    }
  }

  play({ name, fade = 0.15, speed, loop = true }) {
    if (!this.mixer) {
      this.mixer = new THREE.AnimationMixer(this.obj)
      this.ctx.world.setHot(this, true)
    }
    if (this.action?._clip.name === name) {
      return
    }
    if (this.action) {
      this.action.fadeOut(fade)
    }
    this.action = this.actions[name]
    if (!this.action) {
      const clip = this.clips[name]
      if (!clip) return console.warn(`[skinnedmesh] animation not found: ${name}`)
      this.action = this.mixer.clipAction(clip)
      this.actions[name] = this.action
    }
    if (speed !== undefined) this.action.timeScale = speed
    this.action.clampWhenFinished = !loop
    this.action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce)
    this.action.reset().fadeIn(fade).play()
  }

  stop(opts = defaultStopOpts) {
    if (!this.action) return
    this.action.fadeOut(opts.fade)
    this.action = null
  }

  getBoneTransform(name) {
    if (!this.obj) return null
    if (!this.bones) {
      this.bones = {}
      this.obj.traverse(child => {
        if (child.isBone) this.bones[child.name] = child
      })
    }
    const bone = this.bones[name]
    if (!bone) {
      console.warn(`[skinnedmesh] bone not found: ${name}`)
      return null
    }
    // combine the skinned mesh's world matrix with the bone's world matrix
    // return m1.multiplyMatrices(this.matrixWorld, bone.matrixWorld)
    bone.updateMatrixWorld(true)
    return m1.copy(bone.matrixWorld)
  }

  readBone(name) {
    if (!this.obj) return null
    if (!this.bones) {
      this.bones = {}
      this.obj.traverse(child => {
        if (child.isBone) this.bones[child.name] = child
      })
    }
    const bone = this.bones[name]
    if (!bone) {
      console.warn(`[skinnedmesh] bone not found: ${name}`)
      return null
    }
    return bone
  }

  getBone(name) {
    let handle = this.boneHandles[name]
    if (!handle) {
      const self = this
      handle = {
        get position() {
          return self.readBone(name)?.position
        },
        get quaternion() {
          return self.readBone(name)?.quaternion
        },
        get rotation() {
          return self.readBone(name)?.rotation
        },
        get scale() {
          return self.readBone(name)?.scale
        },
        get matrixWorld() {
          const bone = self.readBone(name)
          if (!bone) return null
          bone.updateMatrixWorld(true)
          return bone.matrixWorld
        },
        set matrixWorld(mat) {
          const bone = self.readBone(name)
          if (!bone) return
          bone.matrixAutoUpdate = false
          bone.matrixWorldAutoUpdate = false
          bone.matrixWorld.copy(mat)
        },
        add(pNode) {
          const node = getRef(pNode)
          const bone = self.readBone(name)
          if (!node || !bone) return this
          node._attachObject3D = bone
          self.add(node)
          return this
        },
        remove(pNode) {
          const node = getRef(pNode)
          if (!node) return this
          node._attachObject3D = null
          self.remove(node)
          return this
        },
      }
      this.boneHandles[name] = handle
    }
    return handle
  }

  linkAudioReactivity(sourceId, options = {}) {
    if (!this.ctx.world.audioReactivity) {
      console.warn('[SkinnedMesh] linkAudioReactivity called but AudioReactivity system not ready.')
      return
    }
    this.unlinkAudioReactivity()

    const meshes = []
    if (this.obj) {
      this.obj.traverse(node => {
        if (node.isMesh && node.material) {
          this.ctx.world.audioReactivity.link({
            name: 'skinnedmesh',
            handle: { material: node.material }
          }, sourceId, {
            targetType: 'material',
            property: options.property || 'emissiveIntensity',
            band: options.band || 'volume',
            scale: options.scale ?? 1,
            offset: options.offset ?? 0,
            intensity: options.intensity ?? 1,
            color: options.color,
            from: options.from,
            to: options.to
          })
          meshes.push(node)
        }
      })
    }

    this._audioReactiveMeshes = meshes
  }

  unlinkAudioReactivity() {
    if (!this.ctx.world.audioReactivity) return

    if (this._audioReactiveMeshes) {
      for (const mesh of this._audioReactiveMeshes) {
        this.ctx.world.audioReactivity.unlink({
          name: 'skinnedmesh',
          handle: { material: mesh.material }
        })
      }
      this._audioReactiveMeshes = null
    }
  }

  getProxy() {
    const self = this
    if (!this.proxy) {
      let proxy = {
        get anims() {
          return self.anims
        },
        get url() {
          return self._url
        },
        get castShadow() {
          return self.castShadow
        },
        set castShadow(value) {
          self.castShadow = value
        },
        get receiveShadow() {
          return self.receiveShadow
        },
        set receiveShadow(value) {
          self.receiveShadow = value
        },
        play(opts) {
          self.play(opts)
        },
        stop(opts) {
          self.stop(opts)
        },
        getBoneTransform(name) {
          return self.getBoneTransform(name)
        },
        linkAudioReactivity(sourceId, options) {
          self.linkAudioReactivity(sourceId, options)
        },
        unlinkAudioReactivity() {
          self.unlinkAudioReactivity()
        },
      }
      proxy = Object.defineProperties(proxy, Object.getOwnPropertyDescriptors(super.getProxy())) // inherit Node properties
      // Override getBone to ensure it uses the correct method
      proxy = Object.defineProperties(proxy, {
        getBone: {
          value: (name) => self.getBone(name),
          enumerable: true,
          configurable: true,
        },
      })
      this.proxy = proxy
    }
    return this.proxy
  }
}
